import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import PhoneMockup from "@/components/PhoneMockup";
import { Reveal } from "@/components/Reveal";
import { useSiteConfig } from "@/context/SiteConfigContext";

export default function HowItWorks() {
  const { config } = useSiteConfig();
  const t = config.t.howItWorks;
  const STEPS = config.lists.steps.map((s, i) => ({ ...s, n: String(i + 1).padStart(2, "0") }));
  const [active, setActive] = useState(0);
  const refs = useRef([]);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(Number(e.target.dataset.idx));
        });
      },
      { rootMargin: "-45% 0px -45% 0px", threshold: 0 }
    );
    refs.current.forEach((r) => r && obs.observe(r));
    return () => obs.disconnect();
  }, []);

  return (
    <section id="how-it-works" className="relative bg-white py-28 sm:py-36" data-testid="how-it-works">
      <div className="mx-auto max-w-6xl px-6">
        <Reveal>
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-brand">{t.eyebrow}</p>
          <h2 className="mt-4 max-w-2xl text-4xl font-bold tracking-tighter text-neutral-900 sm:text-5xl lg:text-6xl">
            {t.heading}
          </h2>
        </Reveal>

        <div className="mt-16 grid gap-12 lg:grid-cols-2">
          {/* sticky phone */}
          <div className="hidden lg:block">
            <div className="sticky top-28 flex justify-center">
              <div className="relative">
                <div className="brand-glow absolute inset-0 -z-10 scale-110" />
                <PhoneMockup screen={STEPS[active].screen} animateKey={active} />
              </div>
            </div>
          </div>

          {/* steps */}
          <div className="flex flex-col gap-8 lg:gap-24 lg:pt-10">
            {STEPS.map((s, i) => (
              <div key={s.n} ref={(el) => (refs.current[i] = el)} data-idx={i}
                className="lg:min-h-[45vh] lg:flex lg:flex-col lg:justify-center">
                {/* mobile phone */}
                <div className="mb-6 flex justify-center lg:hidden">
                  <PhoneMockup screen={s.screen} className="scale-90" />
                </div>
                <motion.div
                  animate={{ x: active === i ? 0 : 0 }}
                  className={`border-l-2 pl-5 transition-colors duration-300 ${active === i ? "border-brand" : "border-black/10"}`}>
                  <span className={`font-mono-tech text-sm font-semibold transition-colors duration-300 ${active === i ? "text-brand" : "text-neutral-400"}`}>{s.n}</span>
                  <h3 className="mt-2 text-2xl font-bold tracking-tight text-neutral-900 sm:text-3xl">{s.title}</h3>
                  <p className="mt-3 max-w-md text-lg leading-relaxed text-neutral-500">{s.body}</p>
                </motion.div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
